import { useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import { createAppRouter } from './router'
import { useStore } from './store'
import { ToastStack } from './components/ui'
import { ModalRoot } from './components/modals'
import { bootstrapConsole } from './bootstrap'
import { useThemeSync } from './lib/theme'

// Exported (not just a local const) so tests can reset it between cases —
// see apps/console/test/integration.test.tsx's beforeEach. It is a
// module-level singleton for the app's real lifetime (one browser router,
// created once); task 31's auth gate is the first thing in this app to
// perform real programmatic navigation (`<Navigate>`,
// src/components/auth/AuthGate.tsx), so a test suite that renders `<App/>`
// more than once needs a way to put this same router back at '/' between
// cases, or one test's redirect leaks into the next test's starting point.
export const router = createAppRouter()

/**
 * Root component. Keeps document.title in sync with the runtime-configured
 * product name (config['b.name'], loaded into the store's `cfg` by whatever
 * fetches /ops/config — see docs/design-handoff/README.md; the name is never
 * a literal in this file) and renders the toast stack above the router.
 *
 * Task 27: also runs the console's one-time bootstrap (initial threads,
 * presence, identity, then the live SSE stream — see src/bootstrap.ts for
 * the exact ordering and why it must happen here) and mounts `ModalRoot`
 * once, at this root level, sibling to the whole routed page tree — so a
 * modal can overlay any page regardless of which route is active, per Task
 * 17's own note that nothing had mounted it yet.
 *
 * This must be `App`, not `ConsolePage`: `ConsolePage` mounts and unmounts
 * every time the operator navigates to /settings, /history or a transcript
 * and back, and re-subscribing the stream on every one of those transitions
 * would both waste a connection and create exactly the kind of gap (events
 * missed between the old connection closing and the new one opening) the
 * bootstrap's own ordering work is designed to avoid. `App` wraps
 * `RouterProvider` and is mounted for the whole app's lifetime, so the
 * bootstrap here runs exactly once.
 */
export function App() {
  // Paints this person's stored appearance, and follows the OS while their
  // choice is an Auto one. Mounted here, like the bootstrap below, because
  // App lives for the whole app's lifetime.
  useThemeSync()

  const nameFromCfg = useStore((s) => s.cfg['b.name'])
  const toasts = useStore((s) => s.toasts)

  useEffect(() => {
    document.title = typeof nameFromCfg === 'string' && nameFromCfg.length > 0 ? nameFromCfg : 'Console'
  }, [nameFromCfg])

  useEffect(() => {
    const handle = bootstrapConsole()
    return () => handle.close()
    // Runs once for the app's lifetime — see this component's own doc comment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <RouterProvider router={router} />
      <ToastStack toasts={toasts} />
      <ModalRoot />
    </>
  )
}
